import React from "react";
import { useNavigate } from "react-router-dom";

import { pathColorsFor, readableTextOn } from "../../../utils/crossroadsTheme";
import useLiveHost, { PHASE_LABEL } from "./useLiveHost";
import { isOnline } from "../../../utils/liveFirebase";
import { LearningContent } from "../../../components/crossroads/CrossroadsUi";
import "./LiveControl.css";

/**
 * Admin-run Live Audience game (PRD 2.2 / 7.7). Players log in through their normal game link
 * and land on /vote; every step the admin takes here appears on their screens in real time.
 * Each session has its own live channel (liveChannelFor), so only this session's players follow it.
 * "Present" opens the same game as a full-screen board for a projector (Present.jsx).
 */
export default function LiveControl() {
  const navigate = useNavigate();
  const {
    token,
    content,
    loadError,
    live,
    participants,
    theme,
    questions,
    total,
    phase,
    index,
    question,
    counts,
    online,
    remainingMs,
    isLast,
    correct,
    busy,
    summary,
    startGame,
    releaseQuestion,
    closeVoting,
    reveal,
    showLearning,
    finishGame,
    restart,
    unsavedResults,
    retryUnsaved,
  } = useLiveHost();
  const colors = question ? pathColorsFor(question, theme) : null;

  if (!token) return <div className="lc-page"><p className="lc-empty">Open the admin panel from your game link to run a live game.</p></div>;
  if (loadError) return <div className="lc-page"><p className="lc-error">{loadError}</p></div>;
  if (!content || live === undefined) return <div className="lc-page"><p className="lc-empty">Loading live game…</p></div>;

  const pct = (n) => (counts.total ? Math.round((n / counts.total) * 100) : 0);
  const showVotes = ["closed", "reveal", "learning"].includes(phase);
  const players = Object.entries(participants).filter(([, p]) => isOnline(p));

  return (
    <div className="lc-page">
      <div className="lc-head">
        <div>
          <h1>Live Control</h1>
          <p>
            Players open the game from their normal link and wait on their screen. Everything you start here appears
            for them instantly.
          </p>
        </div>
        <div className="lc-head-actions">
          <button type="button" className="lc-btn lc-btn--primary" onClick={() => navigate("/admin/present")} disabled={!total}>
            <i className="fa-solid fa-display" aria-hidden /> Present
          </button>
          <button type="button" className="lc-btn" onClick={restart} disabled={busy}>
            <i className="fa-solid fa-rotate-left" aria-hidden /> {phase === "complete" ? "New Game" : "Restart"}
          </button>
        </div>
      </div>

      {unsavedResults ? (
        <p className="lc-warn" role="alert">
          <strong>
            {unsavedResults} live result{unsavedResults === 1 ? "" : "s"} not saved to Reports yet.
          </strong>{" "}
          Check your connection — it is retried automatically.{" "}
          <button type="button" className="lc-btn" onClick={retryUnsaved}>
            Retry now
          </button>
        </p>
      ) : null}

      {theme.mode !== "live" ? (
        <p className="lc-warn">
          Game mode is <strong>Self Paced</strong>, so players' Play button opens the self-paced journey instead of this
          live game. Switch to <strong>Live Audience</strong> in Game Settings → General, then Publish.
        </p>
      ) : null}

      <div className="lc-layout">
        <aside className="lc-side">
          <div className="lc-status" role="status">
            <span className={`lc-dot lc-dot--${phase}`} aria-hidden /> {PHASE_LABEL[phase] || phase}
          </div>
          <div className="lc-players">
            <strong>{online}</strong> player{online === 1 ? "" : "s"} online
          </div>
          <ul className="lc-names">
            {players.slice(0, 60).map(([pid, p]) => (
              <li key={pid}>{p.name || "Player"}</li>
            ))}
            {!players.length ? <li className="lc-muted">No one yet</li> : null}
          </ul>
          <h2>Journey</h2>
          <ol className="lc-steps">
            {questions.map((q, i) => {
              const done = phase === "complete" || (live?.questionId != null && i < index) || (i === index && ["reveal", "learning"].includes(phase));
              const current = i === index && ["voting", "closed", "reveal", "learning"].includes(phase);
              return (
                <li key={q.id} className={`${done ? "is-done" : ""}${current ? " is-current" : ""}`}>
                  <span className="lc-step-num">{done ? "✓" : i + 1}</span>
                  <span title={q.question_name}>{q.question_name || `Crossroads ${i + 1}`}</span>
                </li>
              );
            })}
          </ol>
        </aside>

        <section className="lc-main" aria-live="polite">
          {phase === "lobby" ? (
            <div className="lc-card lc-center">
              <h2>Ready when you are</h2>
              <p>
                {online} player{online === 1 ? " is" : "s are"} waiting. Starting shows everyone the introduction; then
                you release the crossroads one by one.
              </p>
              <button type="button" className="lc-btn lc-btn--primary lc-btn--big" disabled={busy || !total} onClick={startGame}>
                <i className="fa-solid fa-play" aria-hidden /> Start Game
              </button>
              {!total ? <p className="lc-error">Add crossroads (and Publish) before starting.</p> : null}
            </div>
          ) : null}

          {phase === "intro" ? (
            <div className="lc-card lc-center">
              <h2>Players are reading the introduction</h2>
              <p className="lc-quote">{theme.introText}</p>
              <button type="button" className="lc-btn lc-btn--primary lc-btn--big" disabled={busy} onClick={() => releaseQuestion(0)}>
                <i className="fa-solid fa-paper-plane" aria-hidden /> Release Crossroads 1
              </button>
            </div>
          ) : null}

          {question ? (
            <div className="lc-card">
              <div className="lc-qhead">
                <div>
                  <div className="lc-eyebrow">
                    Crossroads {index + 1} of {total}
                  </div>
                  <h2>{question.question_name}</h2>
                </div>
                {phase === "voting" ? (
                  <div className="lc-timer">
                    <span>{Math.ceil(remainingMs / 1000)}</span>
                    <small>sec</small>
                  </div>
                ) : null}
              </div>
              <p className="lc-scenario">{question.question_name}</p>

              <div className="lc-options">
                {[
                  ["A", question.option_one, colors.a, counts.a],
                  ["B", question.option_two, colors.b, counts.b],
                ].map(([letter, text, bg, n]) => (
                  <div key={letter} className={`lc-option${showVotes && correct === letter ? " is-correct" : ""}`} style={{ borderColor: bg }}>
                    <div className="lc-option-row">
                      <span className="lc-letter" style={{ background: bg, color: readableTextOn(bg) }}>
                        {letter}
                      </span>
                      <span className="lc-option-text">{text}</span>
                      {correct === letter ? <span className="lc-correct-tag">Correct</span> : null}
                    </div>
                    <div className="lc-bar" aria-hidden>
                      <span style={{ width: `${pct(n)}%`, background: bg }} />
                    </div>
                    <div className="lc-votes">
                      {n} vote{n === 1 ? "" : "s"} · {pct(n)}%
                    </div>
                  </div>
                ))}
              </div>
              <p className="lc-muted">
                {counts.total} of {online} online player{online === 1 ? "" : "s"} voted
                {phase === "voting" ? " so far — players don't see the split until you close voting." : "."}
              </p>

              {phase === "learning" ? (
                <div className="lc-learning">
                  <div className="lc-eyebrow">💡 {question.dyk_heading || theme.dykHeading}</div>
                  <LearningContent question={question} />
                </div>
              ) : null}

              <div className="lc-actions">
                {phase === "voting" ? (
                  <button type="button" className="lc-btn lc-btn--primary lc-btn--big" onClick={closeVoting} disabled={busy}>
                    <i className="fa-solid fa-lock" aria-hidden /> Close Voting
                  </button>
                ) : null}
                {phase === "closed" ? (
                  <button type="button" className="lc-btn lc-btn--primary lc-btn--big" onClick={reveal} disabled={busy}>
                    <i className="fa-solid fa-eye" aria-hidden /> {correct ? "Reveal Answer" : "Show Results"}
                  </button>
                ) : null}
                {phase === "reveal" ? (
                  <button type="button" className="lc-btn lc-btn--primary lc-btn--big" onClick={showLearning} disabled={busy}>
                    <i className="fa-solid fa-lightbulb" aria-hidden /> Show “Did You Know?”
                  </button>
                ) : null}
                {phase === "learning" ? (
                  isLast ? (
                    <button type="button" className="lc-btn lc-btn--primary lc-btn--big" onClick={finishGame} disabled={busy}>
                      <i className="fa-solid fa-flag-checkered" aria-hidden /> Finish Game
                    </button>
                  ) : (
                    <button type="button" className="lc-btn lc-btn--primary lc-btn--big" onClick={() => releaseQuestion(index + 1)} disabled={busy}>
                      <i className="fa-solid fa-paper-plane" aria-hidden /> Release Crossroads {index + 2}
                    </button>
                  )
                ) : null}
              </div>
            </div>
          ) : null}

          {phase === "complete" ? (
            <div className="lc-card">
              <h2>Game finished</h2>
              <div className="lc-kpis">
                <div><strong>{summary.voters}</strong><span>Players voted</span></div>
                <div><strong>{summary.votes}</strong><span>Total votes</span></div>
                <div><strong>{summary.accuracy}%</strong><span>Audience accuracy</span></div>
              </div>
              <ol className="lc-breakdown">
                {summary.rows.map((r) => (
                  <li key={r.q.id}>
                    <span title={r.q.question_name}>{r.q.question_name || `Crossroads ${r.i + 1}`}</span>
                    <span className="lc-bar" aria-hidden>
                      <span style={{ width: `${r.pct ?? 0}%` }} />
                    </span>
                    <strong>{r.pct == null ? "Choice" : `${r.pct}% correct`}</strong>
                  </li>
                ))}
              </ol>
              <p className="lc-muted">Full results are in Reports → Live audience voting.</p>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
